import { Network } from '@capacitor/network'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, onMounted, ref } from 'vue'

import { configApi } from '@/api/client'
import { getErrorMessages } from '@/api/config'
import { useNotifications } from '@/composables/useNotifications'
import mqtt from '@/sync/mqtt'

import { useConfigStore } from './config'

type ErrorMessage = {
  title: string
  description: string
}

export const useStatusStore = defineStore('status', () => {
  const config = useConfigStore()
  const { notifyError, close } = useNotifications()

  const networkConnected = ref(false)
  const mqttConnected = ref(false)
  const errorMessages = ref<ErrorMessage[]>([])
  const hasTimeError = ref(false)

  onMounted(() => {
    Network.addListener('networkStatusChange', status => {
      if (status.connected) {
        networkConnected.value = true
      } else {
        networkConnected.value = false
      }
    })
  })

  const isConnected = computed(() => {
    return (mqttConnected.value && networkConnected.value) || config.demoMode
  })

  async function updateCurrentStatus() {
    const status = await Network.getStatus()
    networkConnected.value = status.connected
    mqttConnected.value = mqtt.isConnected()
    if (networkConnected.value) {
      await checkDeviceTime()
    }
  }

  async function checkDeviceTime() {
    try {
      const { time: serverTime } = await configApi.getTime()
      const timeDifference = Math.abs(new Date(serverTime).getTime() - new Date().getTime())
      hasTimeError.value = timeDifference > 5 * 60 * 1000
    } catch {
      hasTimeError.value = false
    }
  }

  function updateMqttStatus(connected: boolean) {
    mqttConnected.value = connected
  }

  async function refreshErrorMessages(inputErrorMessages?: ErrorMessage[]) {
    close('error')
    errorMessages.value = inputErrorMessages ? inputErrorMessages : await getErrorMessages()
    for (const errorMessage of errorMessages.value) {
      notifyError({
        id: 'error',
        title: errorMessage.title,
        description: errorMessage.description,
        important: true
      })
    }
  }

  return {
    networkConnected,
    mqttConnected,
    errorMessages,
    hasTimeError,
    isConnected,
    updateCurrentStatus,
    updateMqttStatus,
    refreshErrorMessages
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStatusStore, import.meta.hot))
}
